import { GOOGLE_OAUTH_UNAVAILABLE_NOTE, type IntegrationItemDto } from '@fos/shared/contracts/integrations';
import {
  CALENDAR_MEETING_TEXTS,
  INTEGRATION_AUTH_METHOD_LABELS,
  INTEGRATION_GROUP_LABELS,
  INTEGRATION_KIND_DESCRIPTIONS,
  INTEGRATION_SIMULATED_HINT,
  integrationProviderLabel,
  MAILBOX_SCOPE_LABELS,
} from '@fos/shared/labels';
import { CalendarSync, KeyRound, Link2, PenLine, PlugZap, RefreshCw, Settings2, Unplug } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { formatDateShort, formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { Sheet } from '../../../ui/Dialog';
import { Mono } from '../../../ui/Mono';
import { Status } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';
import { DelegateToClaudeButton } from '../../agents/components/DelegateToClaudeButton';
import { integrationDelegation, integrationNeedsClaude } from './delegate-integration';
import { IcsCalendarDialog } from './IcsCalendarDialog';
import { ConfigDialog, ConfirmDialog, ConnectDialog, hasEditableConfig, SecretDialog } from './IntegrationDialogs';
import {
  isIcsCalendar,
  isMailboxKind,
  itemTitle,
  serverOf,
  statusText,
  statusTone,
  testResultToast,
  toastMutationError,
  usesGoogleAccount,
} from './integrations-ui';
import { MailboxHealthSection } from './MailboxHealthSection';
import { MailSignatureDialog, mailboxSignature } from './MailSignatureDialog';

/**
 * Szczegóły integracji (panel boczny po „Ustawienia”): stan, dostawca, konto, klucz („ustawiony …a1B2”, nigdy
 * wartość), kto i kiedy podłączył, ostatnia synchronizacja i błąd; akcje: Testuj, klucz API, ustawienia połączenia,
 * zmiana dostawcy / realne połączenie, Połącz ponownie z Google, przełączenie na symulację, Odłącz. Skrzynka
 * IMAP/SMTP: serwery, login, hasło („ustawione”, nigdy wartość), rodzaj (firmowa / osobista), „Zmień ustawienia
 * skrzynki” (to samo okno co połączenie; puste hasło = zostaje zapisane). Kalendarz z linku iCal: serwer, adres
 * („ustawiony”, nigdy wartość), „Synchronizuj teraz”, „Zmień adres iCal”, Odłącz (spotkania zostają). Skrzynka
 * (17.10.2026): „Stan skrzynki” (na serwerze vs w systemie, ostatni błąd) i „Pobierz starsze maile”.
 */

type Open = 'connect' | 'secret' | 'config' | 'disconnect' | 'simulate' | 'ics' | 'signature' | null;

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line py-2.5">
      <dt className="shrink-0 text-body-s text-fg-muted">{label}</dt>
      <dd className="min-w-0 text-right text-body-s break-words text-fg">{children}</dd>
    </div>
  );
}

export function IntegrationSheet({
  open,
  onOpenChange,
  item,
  googleAvailable,
  onGoogleConnect,
  googlePending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: IntegrationItemDto;
  googleAvailable: boolean;
  onGoogleConnect: (item: IntegrationItemDto) => void;
  googlePending: boolean;
}) {
  const utils = trpc.useUtils();
  const [dialog, setDialog] = useState<Open>(null);
  const test = trpc.integrations.test.useMutation({
    onSuccess: (r) => {
      testResultToast(r);
      void utils.integrations.list.invalidate();
    },
    onError: toastMutationError,
  });
  const disconnect = trpc.integrations.disconnect.useMutation({
    onSuccess: () => {
      toast.success(`Odłączono: ${itemTitle(item)}`);
      setDialog(null);
      void utils.integrations.list.invalidate();
      // „Odłącz” znika po odłączeniu: zamykamy też panel (po oknie potwierdzenia), fokus wraca na wiersz listy
      setTimeout(() => onOpenChange(false), 0);
    },
    onError: toastMutationError,
  });
  const syncNow = trpc.calendar.syncNow.useMutation({
    onSuccess: () => toast.success(CALENDAR_MEETING_TEXTS.syncQueued),
    onError: toastMutationError,
  });
  const simulate = trpc.integrations.switchMode.useMutation({
    onSuccess: () => {
      toast.success('Włączono symulację', { description: INTEGRATION_SIMULATED_HINT });
      setDialog(null);
      void utils.integrations.list.invalidate();
    },
    onError: toastMutationError,
  });

  const server = item.managedBy === 'server';
  const google = usesGoogleAccount(item);
  const mailbox = isMailboxKind(item.kind);
  const imap = item.authMethod === 'password';
  const ics = item.id !== null && isIcsCalendar(item);
  const apiKey = item.authMethod === 'api_key';
  const real = item.id !== null && !item.simulated;
  // dostawcy do wyboru w oknie połączenia (bez logowania Google i konfiguracji serwera)
  const switchable = item.providers.filter((p) => p.auth === 'api_key' || p.auth === 'none' || p.auth === 'password');
  // „Połącz z Google”: kalendarz zawsze (z notką, gdy niedostępne), skrzynka — gdy jest Gmailem albo Google działa
  const showGoogle =
    item.canManage && item.id !== null && (google || ((item.kind === 'google_calendar' || mailbox) && googleAvailable));
  const imapServer = serverOf(item.config, 'imap');
  const smtpServer = serverOf(item.config, 'smtp');
  const smtpPort = item.config.smtpPort;
  const close = (v: boolean) => setDialog(v ? dialog : null);

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={onOpenChange}
        title={itemTitle(item)}
        description={INTEGRATION_KIND_DESCRIPTIONS[item.kind]}
      >
        <div className="flex flex-col gap-1">
          <Status tone={statusTone(item)}>{statusText(item)}</Status>
          {item.simulated && <p className="text-caption text-fg-muted">{INTEGRATION_SIMULATED_HINT}</p>}
          {server && <p className="text-caption text-fg-muted">Ustawiane w konfiguracji serwera (.env).</p>}
        </div>

        <dl className="mt-4 border-t border-line">
          {item.provider && <Row label="Dostawca">{integrationProviderLabel(item.provider)}</Row>}
          {item.summary && (
            <Row label={mailbox ? 'Skrzynka' : google ? 'Konto' : ics ? 'Serwer' : 'Połączenie'}>{item.summary}</Row>
          )}
          {imap && imapServer && <Row label="Serwer IMAP">{imapServer} · SSL/TLS</Row>}
          {imap && smtpServer && (
            <Row label="Serwer SMTP">
              {smtpServer} · {smtpPort === 465 ? 'SSL/TLS' : 'STARTTLS'}
            </Row>
          )}
          {imap && typeof item.config.login === 'string' && <Row label="Login">{item.config.login}</Row>}
          {item.authMethod && item.authMethod !== 'none' && (
            <Row label="Logowanie">{INTEGRATION_AUTH_METHOD_LABELS[item.authMethod]}</Row>
          )}
          {apiKey && (
            <Row label="Klucz API">
              {item.hasSecret ? (
                <>
                  Ustawiony{item.secretLast4 && <Mono className="ml-1">…{item.secretLast4}</Mono>}
                  {item.secretSetAt && (
                    <span className="text-fg-muted"> · zapisany {formatDateShort(item.secretSetAt)}</span>
                  )}
                </>
              ) : (
                'Nieustawiony'
              )}
            </Row>
          )}
          {imap && (
            <Row label="Hasło">
              {item.hasSecret ? (
                <>
                  Ustawione
                  {item.secretSetAt && (
                    <span className="text-fg-muted"> · zapisane {formatDateShort(item.secretSetAt)}</span>
                  )}
                </>
              ) : (
                'Nieustawione'
              )}
            </Row>
          )}
          {ics && (
            <Row label="Adres iCal">
              {item.hasSecret ? (
                <>
                  Ustawiony
                  {item.secretSetAt && (
                    <span className="text-fg-muted"> · zapisany {formatDateShort(item.secretSetAt)}</span>
                  )}
                </>
              ) : (
                'Nieustawiony'
              )}
            </Row>
          )}
          {google && item.id && !item.simulated && (
            <Row label="Dostęp Google">{item.hasSecret ? 'Zapisany' : 'Brak'}</Row>
          )}
          {mailbox && item.id && (
            <Row label="Rodzaj skrzynki">{MAILBOX_SCOPE_LABELS[item.ownerUserId ? 'personal' : 'company']}</Row>
          )}
          {mailbox && item.id && <Row label="Podpis">{mailboxSignature(item) ? 'Ustawiony' : 'Brak'}</Row>}
          <Row label="Zarządza">
            {item.group === 'member' && item.ownerName ? item.ownerName : INTEGRATION_GROUP_LABELS[item.group]}
          </Row>
          {item.connectedBy && (
            <Row label="Podłączył(a)">
              {item.connectedBy.name}
              {item.connectedAt && <span className="text-fg-muted"> · {formatDateShort(item.connectedAt)}</span>}
            </Row>
          )}
          {item.lastSyncAt && <Row label="Synchronizacja">{formatRelativePast(item.lastSyncAt)}</Row>}
          {item.lastError && (
            <Row label="Ostatni błąd">
              <span className="text-danger">{item.lastError}</span>
            </Row>
          )}
        </dl>

        {mailbox && item.id && <MailboxHealthSection item={item} />}

        <div className="mt-6 flex flex-col items-stretch gap-2">
          {item.canTest && (
            <Button
              variant="secondary"
              icon={RefreshCw}
              loading={test.isPending}
              onClick={() => test.mutate(item.id ? { id: item.id } : { kind: item.kind })}
            >
              Testuj połączenie
            </Button>
          )}
          {/* Fala 9: błąd integracji = sprawa operacyjna dla Claude (moduł Agenci, Edycja) */}
          {item.canManage && integrationNeedsClaude(item) && (
            <DelegateToClaudeButton target={() => integrationDelegation(item)} />
          )}
          {item.canManage && apiKey && real && (
            <Button variant="secondary" icon={KeyRound} onClick={() => setDialog('secret')}>
              {item.hasSecret ? 'Zmień klucz API' : 'Ustaw klucz API'}
            </Button>
          )}
          {item.canManage && ics && item.status !== 'disconnected' && (
            <Button
              variant="secondary"
              icon={CalendarSync}
              loading={syncNow.isPending}
              onClick={() => item.id && syncNow.mutate({ id: item.id })}
            >
              {CALENDAR_MEETING_TEXTS.syncNow}
            </Button>
          )}
          {item.canManage && ics && (
            <Button variant="secondary" icon={Link2} onClick={() => setDialog('ics')}>
              {item.hasSecret ? 'Zmień adres iCal' : 'Wklej adres iCal'}
            </Button>
          )}
          {item.canManage && real && imap && (
            <Button variant="secondary" icon={Settings2} onClick={() => setDialog('connect')}>
              Zmień ustawienia skrzynki
            </Button>
          )}
          {item.canManage && mailbox && item.id && (
            <Button variant="secondary" icon={PenLine} onClick={() => setDialog('signature')}>
              {mailboxSignature(item) ? 'Zmień podpis' : 'Ustaw podpis'}
            </Button>
          )}
          {item.canManage && real && !ics && hasEditableConfig(item) && (
            <Button variant="secondary" icon={Settings2} onClick={() => setDialog('config')}>
              Ustawienia połączenia
            </Button>
          )}
          {showGoogle && (
            <>
              <Button
                variant="secondary"
                icon={PlugZap}
                disabled={!googleAvailable}
                loading={googlePending}
                onClick={() => onGoogleConnect(item)}
              >
                {real && google ? 'Połącz ponownie z Google' : 'Połącz z Google'}
              </Button>
              {!googleAvailable && <p className="text-caption text-fg-muted">{GOOGLE_OAUTH_UNAVAILABLE_NOTE}</p>}
            </>
          )}
          {item.canManage && real && !server && !google && !imap && switchable.length > 1 && (
            <Button variant="secondary" icon={PlugZap} onClick={() => setDialog('connect')}>
              Zmień dostawcę
            </Button>
          )}
          {/* jeden przycisk trybu: po przełączeniu to ten sam element (zmienia się etykieta), więc fokus zostaje */}
          {item.canManage && !server && !ics && (real || item.kind !== 'google_calendar') && (
            <Button
              variant={real ? 'ghost' : 'secondary'}
              icon={real ? undefined : PlugZap}
              onClick={() => setDialog(real ? 'simulate' : 'connect')}
            >
              {real ? 'Przełącz na symulację' : 'Przełącz na realne połączenie'}
            </Button>
          )}
          {item.canDisconnect && item.status !== 'disconnected' && (
            <Button variant="ghost" icon={Unplug} onClick={() => setDialog('disconnect')}>
              Odłącz
            </Button>
          )}
        </div>
      </Sheet>

      <ConnectDialog open={dialog === 'connect'} onOpenChange={close} item={item} />
      {ics && <IcsCalendarDialog open={dialog === 'ics'} onOpenChange={close} item={item} />}
      <SecretDialog open={dialog === 'secret'} onOpenChange={close} item={item} />
      <ConfigDialog open={dialog === 'config'} onOpenChange={close} item={item} />
      {mailbox && item.id && <MailSignatureDialog open={dialog === 'signature'} onOpenChange={close} item={item} />}
      <ConfirmDialog
        open={dialog === 'disconnect'}
        onOpenChange={close}
        title={`Odłączyć: ${itemTitle(item)}?`}
        description={
          ics
            ? 'Zapisany adres kalendarza zostanie usunięty. Synchronizacja się zatrzyma, spotkania w systemie zostaną.'
            : google
              ? 'Zapisany dostęp do konta Google zostanie usunięty. Synchronizacja się zatrzyma, dane w systemie zostaną.'
              : imap
                ? 'Zapisane hasło skrzynki zostanie usunięte. Synchronizacja i wysyłka się zatrzymają, dane w systemie zostaną.'
                : apiKey
                  ? 'Zapisany klucz zostanie usunięty. Ponowne połączenie wymaga nowego klucza.'
                  : 'Integracja przestanie działać do ponownego połączenia. Dane w systemie zostaną.'
        }
        confirmLabel="Odłącz"
        pending={disconnect.isPending}
        onConfirm={() => item.id && disconnect.mutate({ id: item.id })}
      />
      <ConfirmDialog
        open={dialog === 'simulate'}
        onOpenChange={close}
        title="Przełączyć na symulację?"
        description="Nic nie będzie wychodzić na zewnątrz. Zapisany klucz, hasło albo dostęp zostanie usunięty."
        confirmLabel="Przełącz"
        pending={simulate.isPending}
        onConfirm={() =>
          item.id && simulate.mutate({ id: item.id, version: item.version ?? undefined, mode: 'simulated' })
        }
      />
    </>
  );
}
